// Impression thermique Bluetooth ESC/POS pour tablette (Chrome/Edge Android).
// Compatible imprimantes 58 mm / 80 mm supportant le protocole standard :
// Epson TM-*, Star mC-*, Xprinter, Rongta, MUNBYN, iMin, GOOJPRT PT-210 / BT-210 (lyrlody, Netum, POSTECH), etc.
// Détecte les principales caractéristiques GATT « Serial Port » utilisées par ces modèles.

// ==== Configuration matérielle GOOJPRT PT-210 (valeurs FIXES, pas d'auto-détection) ====
// Ces valeurs sont câblées en dur pour éviter les erreurs de rendu et respecter la spec constructeur.
export const PAPER_WIDTH_MM = 58;         // Largeur papier : 58mm (rouleau standard)
export const PRINTER_DPI = 203;           // Résolution : 203 DPI = 8 dots/mm
export const PRINTER_PROTOCOL = "ESC/POS"; // Protocole d'impression
export const PRINTER_TRANSPORT = "BLE-GATT (SPP-like)"; // Web Bluetooth = BLE uniquement
export const PAPER_WIDTH_DOTS = 384;      // 58mm × 8 dots/mm = 384 points imprimables
export const LINE_COLS_58 = 32;           // Font A 12x24 : 384 / 12 = 32 caractères / ligne

const PRINTER_KEY = "nold_bt_printer_id";

// ==== Alerte déconnexion (vocal + bip + email) — 1 seul déclenchement par événement ====
// Ce flag garantit qu'une même déconnexion ne spamme ni la voix ni le backend.
// Il est remis à false uniquement après une reconnexion réussie (fin de
// connectBluetoothPrinter). Résiste aux refires natifs de gattserverdisconnected.
let _disconnectNotified = false;

// ==== Déblocage audio pour Android Chrome ====
// speechSynthesis ET AudioContext sont bloqués par le navigateur tant qu'aucun
// geste utilisateur n'a "déverrouillé" l'audio. On préchauffe donc dès qu'un
// utilisateur clique sur "Appairer" ou "Tester la voix" : ça permet à une
// alerte future (déconnexion Bluetooth spontanée) de sonner sans geste.
let _audioUnlocked = false;
let _audioCtx = null;

export function unlockAudioAlert() {
  if (_audioUnlocked) return;
  try {
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      // Utterance silencieuse pour "réveiller" le moteur TTS.
      const warm = new SpeechSynthesisUtterance(" ");
      warm.volume = 0; warm.rate = 1; warm.lang = "fr-BE";
      window.speechSynthesis.speak(warm);
      // Force le chargement des voix (parfois vide au premier appel).
      window.speechSynthesis.getVoices();
    }
  } catch { /* ignore */ }
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) {
      _audioCtx = new Ctx();
      if (_audioCtx.state === "suspended") { _audioCtx.resume().catch(() => {}); }
    }
  } catch { /* ignore */ }
  _audioUnlocked = true;
}

function _beep(freq = 880, durMs = 250, when = 0) {
  try {
    if (!_audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) _audioCtx = new Ctx();
    }
    if (!_audioCtx) return;
    if (_audioCtx.state === "suspended") { _audioCtx.resume().catch(() => {}); }
    const t0 = _audioCtx.currentTime + when;
    const osc = _audioCtx.createOscillator();
    const gain = _audioCtx.createGain();
    osc.type = "square";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.7, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + durMs / 1000);
    osc.connect(gain).connect(_audioCtx.destination);
    osc.start(t0);
    osc.stop(t0 + durMs / 1000 + 0.02);
  } catch { /* ignore */ }
}

function _sirenPattern() {
  // 3 bips aigus/graves alternés — imite une sirène courte, très perceptible.
  _beep(1200, 220, 0.00);
  _beep(800,  220, 0.28);
  _beep(1200, 220, 0.56);
  _beep(800,  220, 0.84);
  _beep(1400, 350, 1.15);
}

function _speakOnce(text) {
  try {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "fr-BE"; u.rate = 1.0; u.pitch = 1.0; u.volume = 1.0;
    try {
      const voices = window.speechSynthesis.getVoices() || [];
      const fr = voices.find((v) => (v.lang || "").toLowerCase().startsWith("fr"));
      if (fr) u.voice = fr;
    } catch { /* ignore */ }
    window.speechSynthesis.speak(u);
  } catch { /* ignore */ }
}

export function speakDisconnectAlert() {
  // Déclenche : sirène audio + vibration + voix répétée 3× (toutes les 4 s).
  // Chaque brique est best-effort ; si l'une échoue, les autres continuent.
  try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
  _sirenPattern();
  try { navigator.vibrate?.([400, 200, 400, 200, 800]); } catch { /* ignore */ }
  const msg = "Attention. Imprimante déconnectée. Veuillez la réveiller.";
  _speakOnce(msg);
  setTimeout(() => { _sirenPattern(); _speakOnce(msg); }, 4200);
  setTimeout(() => { _sirenPattern(); _speakOnce(msg); }, 8400);
}

// Compat interne : les appels existants gardent le nom underscore.
const _speakDisconnectAlert = speakDisconnectAlert;

// Test explicite déclenché par un bouton — geste utilisateur → audio autorisé.
export function testDisconnectAlert() {
  unlockAudioAlert();
  speakDisconnectAlert();
}

async function _notifyBackendDisconnected(printerName) {
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    const token = localStorage.getItem("bsg_token");
    if (!token) return; // pas connecté = pas d'email possible
    await fetch(`${base}/api/restaurateur/printer/disconnected`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ printer_name: printerName || "" }),
      keepalive: true,
    });
  } catch { /* ignore — l'utilisateur voit déjà l'alerte visuelle + vocale */ }
}

async function _notifyBackendReconnected(printerName) {
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    const token = localStorage.getItem("bsg_token");
    if (!token) return;
    await fetch(`${base}/api/restaurateur/printer/reconnected`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ printer_name: printerName || "" }),
      keepalive: true,
    });
  } catch { /* ignore */ }
}

// UUIDs génériques ESC/POS Bluetooth (couvre >99% des imprimantes du marché)
// Web Bluetooth exige que le service soit listé dans optionalServices AVANT connexion
// sinon getPrimaryServices() ne retourne rien même après appairage réussi.
const KNOWN_SERVICES = [
  // Services 16-bit standards (imprimantes GOOJPRT, BT-210, Xprinter, Rongta, Munbyn…)
  0xFF00, 0xFEE7, 0xFEE0, 0xFF10, 0xFFB0, 0xFFE0, 0x18F0, 0xAF30, 0x1101,
  0x49535343,
  // Services 128-bit connus des principaux fabricants
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2",           // Star Bluetooth 4.x
  "000018f0-0000-1000-8000-00805f9b34fb",           // ESC/POS standard (GOOJPRT PT-210 / BT-210 / lyrlody)
  "6e400001-b5a3-f393-e0a9-e50e24dcca9e",           // Nordic UART (clones BT-210)
  "49535343-fe7d-4ae5-8fa9-9fafd205e455",           // ISSC / Microchip Transparent UART
  "0000ffe0-0000-1000-8000-00805f9b34fb",           // HM-10 / cheap BLE modules
  "0000ffb0-0000-1000-8000-00805f9b34fb",           // Alt Nordic-like sur certains BT-210
  "0000ff00-0000-1000-8000-00805f9b34fb",           // Xprinter / Rongta 58mm alt
  "0000ff10-0000-1000-8000-00805f9b34fb",           // Zjiang / iMin
  "0000af30-0000-1000-8000-00805f9b34fb",           // Zjiang alt
  "0000fee7-0000-1000-8000-00805f9b34fb",           // Xiaomi thermal / MUNBYN
  "0000fee0-0000-1000-8000-00805f9b34fb",           // Xiaomi generic
  "0000ffe5-0000-1000-8000-00805f9b34fb",           // Cashino / Beeprt
  "02f00000-0000-0000-0000-00000000ff00",           // Rongta RP58BU
  "0000fff0-0000-1000-8000-00805f9b34fb",           // Sunmi / Netum
  // GENERIC ATTRIBUTE PROFILE — permet de scanner tous les services exposés
  "00001800-0000-1000-8000-00805f9b34fb",           // Generic Access
  "00001801-0000-1000-8000-00805f9b34fb",           // Generic Attribute
];

// Caractéristiques d'écriture couramment exposées
const WRITE_CHARS = [
  0xFF02, 0xFEE1, 0x2AF1,
  "bef8d6c9-9c21-4c9e-b632-bd58c1009f9f",
  "00002af1-0000-1000-8000-00805f9b34fb",
  "6e400002-b5a3-f393-e0a9-e50e24dcca9e",           // Nordic UART TX
  "49535343-8841-43f4-a8d4-ecbe34729bb3",           // ISSC RX
  "0000ffe1-0000-1000-8000-00805f9b34fb",           // HM-10 data
];

let _state = { device: null, characteristic: null, connected: false, name: null };

export function getPrinterStatus() {
  return {
    connected: !!(_state.connected && _state.characteristic),
    name: _state.name || "",
    uuid: _state.characteristic?.uuid || "",
    serviceUuid: _state.characteristic?.service?.uuid || "",
  };
}


export async function connectBluetoothPrinter() {
  if (!("bluetooth" in navigator)) {
    throw new Error("Ce navigateur ne supporte pas Web Bluetooth. Utilisez Chrome ou Edge sur Android/Windows.");
  }
  // Préchauffe audio dès le geste utilisateur (Android Chrome bloque
  // speechSynthesis + AudioContext tant qu'aucun click n'a eu lieu).
  unlockAudioAlert();
  // 1) Reconnexion silencieuse : si une imprimante a déjà été autorisée dans cette app,
  // navigator.bluetooth.getDevices() la retourne SANS popup (Chrome 89+).
  // Permet à un restaurateur de retrouver son imprimante après un reboot tablette
  // ou une fermeture d'onglet, sans avoir à re-appairer.
  try {
    if (typeof navigator.bluetooth.getDevices === "function") {
      const known = await navigator.bluetooth.getDevices();
      const savedId = (() => { try { return localStorage.getItem(PRINTER_KEY); } catch { return null; } })();
      const preferred = savedId ? known.find((d) => d.id === savedId) : null;
      const target = preferred || known[0];
      if (target) {
        try {
          const server = await target.gatt.connect();
          const svc = await server.getPrimaryServices();
          const cand = [];
          for (const s of svc) {
            try {
              for (const c of await s.getCharacteristics()) {
                if (c.properties?.write || c.properties?.writeWithoutResponse) cand.push(c);
              }
            } catch { /* ignore */ }
          }
          for (const uuid of RECONNECT_PREFERRED) {
            const h = cand.find((c) => (c.uuid || "").toLowerCase() === uuid);
            if (h) {
              _state = { device: target, characteristic: h, connected: true, name: target.name || "Imprimante Bluetooth" };
              return _state;
            }
          }
          if (cand.length) {
            _state = { device: target, characteristic: cand[0], connected: true, name: target.name || "Imprimante Bluetooth" };
            return _state;
          }
        } catch { /* fallback vers requestDevice */ }
      }
    }
  } catch { /* getDevices peut être bloqué par certaines politiques — fallback silencieux */ }

  let device;
  // Stratégie : filtres par services (imprimantes conformes) + filtres par NAME PREFIX
  // pour capter la GOOJPRT PT-210 et autres imprimantes chinoises qui annoncent uniquement
  // leur nom sans services BLE dans l'advertising packet. Chrome OR les objets du tableau filters,
  // donc UN SEUL popup suffit — on garde le geste utilisateur pour requestDevice.
  const _combinedFilters = [
    ...KNOWN_SERVICES.map((s) => ({ services: [s] })),
    { namePrefix: "PT-" },
    { namePrefix: "MPT-" },
    { namePrefix: "MTP-" },
    { namePrefix: "GOOJPRT" },
    { namePrefix: "Printer" },
    { namePrefix: "BlueTooth" },
    { namePrefix: "58Printer" },
    { namePrefix: "80Printer" },
    { namePrefix: "XP-" },
    { namePrefix: "RPP" },
    { namePrefix: "InnerPrinter" },
  ];
  try {
    device = await navigator.bluetooth.requestDevice({
      filters: _combinedFilters,
      optionalServices: KNOWN_SERVICES,
    });
  } catch (e1) {
    // Fallback : scan large (montre TOUS les BLE) — utile pour imprimantes au nom exotique
    if (e1?.name === "NotFoundError") {
      try {
        device = await navigator.bluetooth.requestDevice({
          acceptAllDevices: true,
          optionalServices: KNOWN_SERVICES,
        });
      } catch (e2) {
        if (e2?.name === "NotFoundError") {
          throw new Error(
            "Aucune imprimante détectée. Vérifications rapides :\n" +
            "1) L'imprimante est ALLUMÉE et à moins de 3 m\n" +
            "2) Voyant Bluetooth CLIGNOTE (mode appairage) — sinon éteignez/rallumez\n" +
            "3) Si elle est appairée dans Réglages Android → Bluetooth, DÉTACHEZ-la (elle bloque le BLE)\n" +
            "4) Recommencez depuis « Appairer imprimante » (bouton de l'app, PAS les réglages Android)"
          );
        }
        throw e2;
      }
    } else {
      throw e1;
    }
  }

  const server = await device.gatt.connect();
  // Priorité aux caractéristiques ESC/POS standard : 0x2AF1, 0xFF02, 0xFEE1, 0x49535343-1E4D...
  // On garde une liste ordonnée pour trouver LA bonne, pas seulement la première writable.
  const PREFERRED_UUIDS = [
    // ISSC Transparent UART RX (GOOJPRT PT-210 confirmé — canal ESC/POS effectif)
    "49535343-8841-43f4-a8d4-ecbe34729bb3",
    "00002af1-0000-1000-8000-00805f9b34fb", // 0x2AF1 standard ESC/POS (BT-210 / GOOJPRT / lyrlody / Xprinter / Munbyn)
    "0000ff02-0000-1000-8000-00805f9b34fb", // 0xFF02 (Rongta / Xprinter)
    "0000ff01-0000-1000-8000-00805f9b34fb", // 0xFF01 alt
    "0000ff11-0000-1000-8000-00805f9b34fb", // Zjiang / iMin write
    "0000fee1-0000-1000-8000-00805f9b34fb", // 0xFEE1
    "0000ffb2-0000-1000-8000-00805f9b34fb", // BT-210 alt write
    "0000ffe1-0000-1000-8000-00805f9b34fb", // HM-10 data (58mm chinois)
    "0000ffe3-0000-1000-8000-00805f9b34fb", // HM-10 alt
    "6e400002-b5a3-f393-e0a9-e50e24dcca9e", // Nordic UART TX (clones cheap BLE)
    "bef8d6c9-9c21-4c9e-b632-bd58c1009f9f", // Star / PT-210 alt
    "0000fff2-0000-1000-8000-00805f9b34fb", // Sunmi / Netum write
    "02f00000-0000-0000-0000-00000000ff02", // Rongta RP58BU write
  ];
  let characteristic = null;
  const allChars = [];
  let servicesCount = 0;
  const services = await server.getPrimaryServices();
  servicesCount = services.length;
  for (const svc of services) {
    try {
      const chars = await svc.getCharacteristics();
      for (const ch of chars) {
        const canWrite = ch.properties?.write || ch.properties?.writeWithoutResponse;
        if (canWrite) allChars.push(ch);
      }
    } catch { /* ignore */ }
  }
  // 1) Cherche d'abord une UUID connue
  for (const uuid of PREFERRED_UUIDS) {
    const hit = allChars.find((c) => (c.uuid || "").toLowerCase() === uuid);
    if (hit) { characteristic = hit; break; }
  }
  // 2) Sinon, prend la première writable disponible
  if (!characteristic && allChars.length) characteristic = allChars[0];
  if (!characteristic) {
    try { device.gatt.disconnect(); } catch { /* ignore */ }
    // Message d'erreur détaillé — aide au diagnostic terrain
    if (servicesCount === 0) {
      throw new Error(
        `L'imprimante « ${device.name || "?"} » a bien été appairée mais ne partage aucun service ` +
        `avec l'app. Modèle inconnu — envoyez son nom exact au support Nold Go pour l'ajouter à la liste.`
      );
    }
    throw new Error(
      `L'imprimante « ${device.name || "?"} » (${servicesCount} services détectés) n'expose pas de canal ` +
      `d'écriture ESC/POS. Vérifiez que c'est bien une imprimante thermique 58/80mm compatible.`
    );
  }

  device.addEventListener("gattserverdisconnected", () => {
    // On perd la référence de la caractéristique mais on garde le device pour tenter une reconnexion silencieuse
    // lors de la prochaine impression.
    _state.characteristic = null;
    _state.connected = false;
    // Alerte double (vocal + email) — 1 seule fois par événement de déconnexion.
    if (!_disconnectNotified) {
      _disconnectNotified = true;
      const pname = _state.name || device.name || "Imprimante Bluetooth";
      _speakDisconnectAlert();
      _notifyBackendDisconnected(pname);
    }
  });

  _state = { device, characteristic, connected: true, name: device.name || "Imprimante Bluetooth" };
  // Reconnexion réussie : ré-arme le flag et signale au backend que l'incident est clos.
  if (_disconnectNotified) {
    _disconnectNotified = false;
    _notifyBackendReconnected(_state.name);
  }
  // Log DEV : quelle caractéristique GATT a été retenue (utile pour diagnostiquer les modèles non-standard).
  try {
    // eslint-disable-next-line no-console
    console.info(`[btPrinter] Connected. Service→Char: ${characteristic?.service?.uuid} → ${characteristic?.uuid} (write=${characteristic?.properties?.write}, wwr=${characteristic?.properties?.writeWithoutResponse})`);
  } catch { /* ignore */ }
  try { localStorage.setItem(PRINTER_KEY, device.id); } catch {}
  return _state;
}


function encodeChunk(bytes) {
  return new Uint8Array(bytes);
}

async function _ensureConnected() {
  if (_state.connected && _state.characteristic && _state.device?.gatt?.connected) return;
  // Tentative de reconnexion silencieuse à la dernière imprimante appairée
  if (_state.device) {
    try {
      const server = await _state.device.gatt.connect();
      const services = await server.getPrimaryServices();
      const allChars = [];
      for (const svc of services) {
        try {
          const chars = await svc.getCharacteristics();
          for (const ch of chars) {
            if (ch.properties?.write || ch.properties?.writeWithoutResponse) {
              allChars.push(ch);
            }
          }
        } catch { /* ignore */ }
      }
      // 1) Priorité aux UUIDs connues (GOOJPRT 0x2AF1, Rongta, etc.)
      for (const uuid of RECONNECT_PREFERRED) {
        const hit = allChars.find((c) => (c.uuid || "").toLowerCase() === uuid);
        if (hit) { _state.characteristic = hit; _state.connected = true; return; }
      }
      // 2) Fallback : première writable
      if (allChars.length) { _state.characteristic = allChars[0]; _state.connected = true; return; }
    } catch { /* ignore */ }
  }
  throw new Error("Imprimante déconnectée — cliquez sur « Appairer imprimante » pour la reconnecter.");
}

async function writeChunks(uint8) {
  await _ensureConnected();
  const size = 20; // MTU sûr pour la plupart des imprimantes BLE 58mm
  const prop = _state.characteristic.properties;
  const preferAck = !prop.writeWithoutResponse && !!prop.write;
  for (let i = 0; i < uint8.length; i += size) {
    const chunk = uint8.slice(i, i + size);
    let attempt = 0;
    while (true) {
      try {
        if (preferAck) {
          await _state.characteristic.writeValue(chunk);
        } else {
          await _state.characteristic.writeValueWithoutResponse(chunk);
        }
        break;
      } catch (e) {
        attempt += 1;
        if (attempt >= 2) throw e;
        await _ensureConnected();
      }
    }
    // ~50 ms entre chunks pour éviter la saturation du buffer BLE des imprimantes 58mm.
    await new Promise((r) => setTimeout(r, 50));
  }
}


// UUIDs prioritaires pour la reconnexion silencieuse (même priorité que l'appairage initial)
const RECONNECT_PREFERRED = [
  "49535343-8841-43f4-a8d4-ecbe34729bb3", // ISSC RX (GOOJPRT PT-210 confirmé)
  "00002af1-0000-1000-8000-00805f9b34fb",
  "0000ff02-0000-1000-8000-00805f9b34fb",
  "0000ff01-0000-1000-8000-00805f9b34fb",
  "0000ff11-0000-1000-8000-00805f9b34fb",
  "0000fee1-0000-1000-8000-00805f9b34fb",
  "0000ffb2-0000-1000-8000-00805f9b34fb",
  "0000ffe1-0000-1000-8000-00805f9b34fb",
  "6e400002-b5a3-f393-e0a9-e50e24dcca9e",
];


// ---- Commandes ESC/POS ----
const ESC = 0x1B, GS = 0x1D, LF = 0x0A;
const INIT = [ESC, 0x40];                     // reset
// Configuration explicite 58mm (GOOJPRT PT-210 / BT-210 / clones 58mm standard) :
// - GS L 0 0 : Marge gauche = 0
// - ESC ! 0 : Police A normale (12 dots de large → 32 caractères / ligne à 58mm)
const SETUP_58MM = [
  GS, 0x4C, 0x00, 0x00,        // GS L : left margin = 0
  ESC, 0x21, 0x00,             // ESC ! 0 : Font A normal (12x24, 32 cols)
];
const CENTER = [ESC, 0x61, 0x01];
const LEFT   = [ESC, 0x61, 0x00];
const BOLD_ON  = [ESC, 0x45, 0x01];
const BOLD_OFF = [ESC, 0x45, 0x00];
const SIZE_2X = [GS, 0x21, 0x11];             // double haut+larg
const SIZE_1X = [GS, 0x21, 0x00];
const CUT = [GS, 0x56, 0x42, 0x00];           // full cut

// Largeur ligne 58mm : 32 caractères max par ligne (Font A 12 dots × 32 = 384 dots)
const LINE_WIDTH_58 = LINE_COLS_58;
const SEP_LINE = "-".repeat(LINE_WIDTH_58);   // 32 tirets exactement

function truncLine(s, n = LINE_WIDTH_58) {
  s = String(s || "");
  return s.length > n ? s.slice(0, n) : s;
}

// Aligne un libellé à gauche et une valeur à droite sur 32 caractères max.
function twoCol(left, right, width = LINE_WIDTH_58) {
  left = String(left || "");
  right = String(right || "");
  if (left.length + right.length + 1 > width) {
    left = left.slice(0, Math.max(0, width - right.length - 1));
  }
  const pad = Math.max(1, width - left.length - right.length);
  return left + " ".repeat(pad) + right;
}

function textToBytes(s) {
  // encodage CP437 approché : caractères ASCII + latin-1 basique
  const out = [];
  for (const ch of s) {
    const code = ch.charCodeAt(0);
    if (code < 128) out.push(code);
    else if (code === 0x20AC) out.push(0xD5);   // €
    else if (code >= 0xC0 && code <= 0xFF) out.push(code); // latin-1 direct
    else out.push(0x3F); // ?
  }
  return out;
}


// ==== Impression Bluetooth uniquement (Web Bluetooth direct, sans app tierce) ====
// Dispatcher : envoie les octets ESC/POS à l'imprimante Bluetooth appairée.
async function _send(buf) {
  await writeChunks(encodeChunk(buf));
}


export async function printOrderTicket(order) {
  if (!_state.connected) throw new Error("Aucune imprimante Bluetooth appairée.");
  const items = order.items || order.cart || [];
  const total = order.total_eur ?? order.total_amount ?? order.fare ?? order.amount_eur ?? 0;
  const requested = order.delivery_at || order.requested_at || null;
  const displayTime = requested
    ? new Date(requested).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "Dès que possible";

  const buf = [];
  const add = (arr) => arr.forEach((b) => buf.push(b));
  const line = (txt) => { add(textToBytes(txt)); buf.push(LF); };

  add(INIT);
  add(SETUP_58MM);
  add(CENTER);
  add(BOLD_ON); add(SIZE_2X);
  line("NOLD GO");
  add(SIZE_1X); add(BOLD_OFF);
  line(truncLine(order.restaurant_name || ""));
  line(SEP_LINE);
  add(LEFT);

  add(BOLD_ON);
  line(truncLine(`Commande #${(order.id || "").slice(0, 8).toUpperCase()}`));
  add(BOLD_OFF);
  line(truncLine(`Recue: ${new Date().toLocaleString("fr-FR")}`));
  line(truncLine(`Livraison: ${displayTime}`));
  line(truncLine(`Client: ${order.passenger_name || order.client_name || "Client"}`));
  if (order.passenger_phone || order.client_phone) line(truncLine(`Tel: ${order.passenger_phone || order.client_phone}`));
  const addr = order.dropoff_address || order.delivery_address;
  if (addr) {
    // Adresse potentiellement longue : on wrap sur 2 lignes 32 chars
    const a1 = String(addr).slice(0, LINE_WIDTH_58);
    const a2 = String(addr).slice(LINE_WIDTH_58, LINE_WIDTH_58 * 2);
    line(a1);
    if (a2) line(a2);
  }
  line(SEP_LINE);

  add(BOLD_ON);
  line("PLATS COMMANDES");
  add(BOLD_OFF);
  if (items.length === 0) {
    line("Details non disponibles");
  } else {
    for (const it of items) {
      const qty = it.quantity || 1;
      // Font A 32 chars : "99x " (4) + name (max 28) = 32 chars
      const name = String(it.name || it.title || "Plat").slice(0, 28);
      const priceVal = (it.price_eur || it.price || 0) * qty;
      line(truncLine(`${qty}x ${name}`));
      // Prix aligné à droite sur 32 chars : "  1.50 EUR" à droite
      line(twoCol("", `${priceVal.toFixed(2)} EUR`));
      if (it.note) line(truncLine(`  > ${it.note}`));
    }
  }

  line(SEP_LINE);
  add(BOLD_ON); add(SIZE_2X);
  // En taille 2X, 16 caractères max (32/2). "TOTAL 999.99 EUR" = 16 chars pile.
  line(truncLine(`TOTAL ${Number(total).toFixed(2)} EUR`, 16));
  add(SIZE_1X); add(BOLD_OFF);
  line("");
  add(CENTER);
  line("Merci de servir avec Nold Go");
  line("noldgo.com");
  line("");
  line("");
  line("");
  add(CUT);

  await _send(buf);
}


export async function printTestTicket(restaurantName = "Nold Go") {
  if (!_state.connected) throw new Error("Aucune imprimante Bluetooth appairée.");
  const buf = [];
  const add = (arr) => arr.forEach((b) => buf.push(b));
  const line = (txt) => { add(textToBytes(txt)); buf.push(LF); };

  add(INIT); add(SETUP_58MM); add(CENTER);
  add(BOLD_ON); add(SIZE_2X);
  line("NOLD GO");
  add(SIZE_1X); add(BOLD_OFF);
  line("--- TEST IMPRESSION ---");
  line("");
  line(truncLine(restaurantName));
  line(truncLine(new Date().toLocaleString("fr-FR")));
  line(SEP_LINE);
  add(LEFT);
  add(BOLD_ON);
  line("Verifications rapides :");
  add(BOLD_OFF);
  line("[ ] Papier bien aligne");
  line("[ ] Encre / thermique lisible");
  line("[ ] Coupe automatique OK");
  line("[ ] Vitesse de sortie normale");
  line(SEP_LINE);
  add(CENTER);
  add(BOLD_ON); add(SIZE_2X);
  line("OK !");
  add(SIZE_1X); add(BOLD_OFF);
  line("");
  line("Imprimante prete pour");
  line("les commandes clients.");
  line("");
  line("noldgo.com");
  line(""); line(""); line("");
  add(CUT);

  await _send(buf);
}
